import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Disclaimer, Spinner, EmptyState } from "@/components/shared";
import { Bookmark, BookmarkCheck, ArrowRight, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  provincial_funding: "Provincial funding", tax_credit: "Tax credit", benefit: "Benefit",
  financial: "Financial planning", school_support: "School support",
};
const JURIS = { ON: "Ontario", CA: "Federal (Canada)" };

export default function FundingFinder() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [juris, setJuris] = useState("all");
  const [savedOnly, setSavedOnly] = useState(false);

  const load = async () => {
    const { data } = await api.get("/programs");
    setPrograms(data.programs);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleSave = async (p) => {
    const { data } = await api.post(`/programs/${p.id}/save`);
    setPrograms((ps) => ps.map((x) => (x.id === p.id ? { ...x, saved: data.saved } : x)));
    toast.success(data.saved ? "Saved to review." : "Removed from saved.");
  };

  const cats = ["all", ...new Set(programs.map((p) => p.category))];
  const savedCount = programs.filter((p) => p.saved).length;
  const filtered = programs.filter((p) =>
    (cat === "all" || p.category === cat) &&
    (juris === "all" || p.jurisdiction === juris) &&
    (!savedOnly || p.saved));

  if (loading) return <Spinner label="Loading programs…" />;

  return (
    <div>
      <PageHeader eyebrow="Funding & tax credits" title="Funding Finder"
        subtitle="Understand the programs, tax credits and supports that may be worth reviewing." />

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="funding-filters">
        <div className="relative">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="funding-category-select"
            className="h-11 rounded-xl border border-kid-line bg-white text-kid-navy text-sm font-poppins pl-4 pr-10 appearance-none outline-none focus:border-kid-teal cursor-pointer">
            {cats.map((c) => <option key={c} value={c}>{c === "all" ? "All categories" : CATEGORY_LABELS[c] || c}</option>)}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-kid-muted pointer-events-none" />
        </div>

        <div className="relative">
          <select value={juris} onChange={(e) => setJuris(e.target.value)} data-testid="funding-region-select"
            className="h-11 rounded-xl border border-kid-line bg-white text-kid-navy text-sm font-poppins pl-4 pr-10 appearance-none outline-none focus:border-kid-teal cursor-pointer">
            <option value="all">All regions</option>
            {["ON", "CA"].map((j) => <option key={j} value={j}>{JURIS[j]}</option>)}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-kid-muted pointer-events-none" />
        </div>

        <button onClick={() => setSavedOnly(!savedOnly)} data-testid="funding-saved-toggle"
          className={`inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-sm font-poppins font-semibold border transition-all ${savedOnly ? "bg-kid-sun text-kid-navy border-kid-sun" : "bg-white text-kid-navy border-kid-line hover:border-kid-teal"}`}>
          {savedOnly ? <BookmarkCheck size={16} /> : <Bookmark size={16} />} Saved {savedCount > 0 ? `(${savedCount})` : ""}
        </button>

        {(cat !== "all" || juris !== "all" || savedOnly) && (
          <button onClick={() => { setCat("all"); setJuris("all"); setSavedOnly(false); }} data-testid="funding-clear-filters"
            className="text-sm font-poppins font-semibold text-kid-teal hover:underline">Clear</button>
        )}

        <span className="ml-auto text-sm text-kid-muted font-poppins" data-testid="funding-result-count">
          {filtered.length} of {programs.length} programs
        </span>
      </div>

      {filtered.length === 0 ? (
        savedOnly && savedCount === 0 ? (
          <EmptyState icon={BookmarkCheck} testid="funding-saved-empty" title="You haven't saved any programs yet"
            body="Tap the bookmark on any program to keep it here for later — your saved programs also show up on your dashboard."
            action={<button onClick={() => setSavedOnly(false)} className="kid-btn kid-btn-primary h-12 px-7" data-testid="funding-browse-programs"><Search size={16} /> Browse all programs</button>} />
        ) : (
          <EmptyState icon={Search} testid="funding-empty" title="No programs match these filters"
            body="Try clearing one or more filters."
            action={<button onClick={() => { setCat("all"); setJuris("all"); setSavedOnly(false); }} className="kid-btn kid-btn-ghost h-11 px-5">Clear filters</button>} />
        )
      ) : (
        <div className="grid md:grid-cols-2 gap-5 kid-stagger">
          {filtered.map((p) => (
            <div key={p.id} className="kid-card kid-card-interactive p-6 flex flex-col" data-testid="program-card">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex gap-2">
                  <span className="text-[.68rem] font-poppins font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-kid-skytint text-kid-teal">{JURIS[p.jurisdiction] || p.jurisdiction}</span>
                  <span className="text-[.68rem] font-poppins font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-kid-mist text-kid-muted">{CATEGORY_LABELS[p.category] || p.category}</span>
                </div>
                <button onClick={() => toggleSave(p)} data-testid="program-save-button" className="text-kid-muted hover:text-kid-teal shrink-0" aria-label="Save program">
                  {p.saved ? <BookmarkCheck className="text-kid-teal" size={20} /> : <Bookmark size={20} />}
                </button>
              </div>
              <h3 className="font-bold text-kid-navy text-lg">{p.name}</h3>
              <p className="text-sm text-kid-muted mt-2 flex-1">{p.plain_language_summary}</p>
              <Link to={`/app/funding/${p.id}`} data-testid="program-view-link" className="inline-flex items-center gap-1.5 text-kid-teal font-poppins font-semibold text-sm mt-4">
                Learn more <ArrowRight size={15} />
              </Link>
            </div>
          ))}
        </div>
      )}

      <div className="mt-7">
        <Disclaimer>These programs may be worth reviewing based on your answers. Eligibility is determined by the program administrator — KidPath does not determine eligibility or guarantee funding.</Disclaimer>
      </div>
    </div>
  );
}
